import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { memo, useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-aria-components';
import { Line } from 'react-chartjs-2';

import { merge } from '@/components';
import { Button, Flex, ListBox, ListBoxItem, Popover, Select, SelectValue, Text } from '@/components/core';
import { ArrowIcon, ChevronIcon } from '@/components/icons';
import { useGetAgentFromPath, useTimezone } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { capitalize, DivProps, findModelId, OmitChildren } from '@/utils';

import { useChangeVariant } from './useChangeVariant';
import { useStatistics } from './useStatistics';
import { createMetric, getData, statisticToName } from './utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

const METRIC_TYPE_VALUES = ['automation', 'flagged', 'edits'] as const;
type MetricType = (typeof METRIC_TYPE_VALUES)[number];

export type FieldGraphProps = OmitChildren<DivProps> & {
  dateRange: DateRange | null;
  dateSelection: number | string;
};

const IFieldGraph = ({ dateRange, dateSelection, className, ...rest }: FieldGraphProps) => {
  const { data: agent } = useGetAgentFromPath();
  const { data: model } = useGetModel(toModelId(findModelId(agent)));
  const timezone = useTimezone();

  const { statistics, previousStatistics } = useStatistics({ dateRange, dateSelection });

  const rows = useMemo(() => {
    const data = [];

    for (const [fieldId, config] of Object.entries(model?.fieldConfig ?? {})) {
      if (config.type === 'table') {
        for (const [subFieldId, subConfig] of Object.entries(config.fields ?? {})) {
          const id = `${fieldId}/${subFieldId}`;
          const subFieldData = getData(statistics?.statistics.fields[id]);
          const previousSubFieldData = getData(previousStatistics?.statistics.fields[id]);

          data.push({
            id,
            automation: createMetric(
              subFieldData.numValuesAutomated,
              subFieldData.numValues,
              previousSubFieldData.numValuesAutomated,
              previousSubFieldData.numValues
            ),
            flagged: createMetric(
              subFieldData.numValuesFlagged,
              subFieldData.numValues,
              previousSubFieldData.numValuesFlagged,
              previousSubFieldData.numValues
            ),
            edits: createMetric(
              subFieldData.numValuesEdited,
              subFieldData.numValues,
              previousSubFieldData.numValuesEdited,
              previousSubFieldData.numValues
            ),
            ...subConfig,
            name: `${config.name} / ${subConfig.name}`,
          });
        }
      } else {
        const fieldData = getData(statistics?.statistics.fields[fieldId]);
        const previousFieldData = getData(previousStatistics?.statistics.fields[fieldId]);

        data.push({
          id: fieldId,
          automation: createMetric(
            fieldData.numValuesAutomated,
            fieldData.numValues,
            previousFieldData.numValuesAutomated,
            previousFieldData.numValues
          ),
          flagged: createMetric(
            fieldData.numValuesFlagged,
            fieldData.numValues,
            previousFieldData.numValuesFlagged,
            previousFieldData.numValues
          ),
          edits: createMetric(
            fieldData.numValuesEdited,
            fieldData.numValues,
            previousFieldData.numValuesEdited,
            previousFieldData.numValues
          ),
          ...config,
        });
      }
    }
    return data;
  }, [model?.fieldConfig, statistics, previousStatistics]);

  const [selectedField, setSelectedField] = useState<string | undefined>();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('automation');

  useEffect(() => {
    if (selectedField == null && rows.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedField(rows[0].id);
    }
  }, [rows, selectedField]);

  const { fieldLabels, fieldAutomationPerDay, fieldFlaggedPerDay, fieldEditsPerDay } = useMemo(() => {
    if (!dateRange) return {};

    const perDay = statistics?.statistics.fieldsPerDay;
    const fieldLabels = [];
    const fieldAutomationPerDay: Record<string, number[]> = {};
    const fieldFlaggedPerDay: Record<string, number[]> = {};
    const fieldEditsPerDay: Record<string, number[]> = {};

    const start = dateRange.start.toDate(timezone);
    const end = dateRange.end.toDate(timezone);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const isoDate = d.toISOString().slice(0, 10);
      fieldLabels.push(isoDate);
    }

    for (const fieldId of rows.map((row) => row.id)) {
      fieldAutomationPerDay[fieldId] = [];
      fieldFlaggedPerDay[fieldId] = [];
      fieldEditsPerDay[fieldId] = [];

      for (const label of fieldLabels) {
        const data = getData(perDay?.[label]?.[fieldId]);

        fieldAutomationPerDay[fieldId].push(data.fractionValuesAutomated);
        fieldFlaggedPerDay[fieldId].push(data.fractionValuesFlagged);
        fieldEditsPerDay[fieldId].push(data.fractionValuesEdited);
      }
    }

    return {
      fieldLabels,
      fieldAutomationPerDay,
      fieldFlaggedPerDay,
      fieldEditsPerDay,
    };
  }, [dateRange, statistics?.statistics.fieldsPerDay, timezone, rows]);

  const fieldOptions = useMemo(() => {
    return {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: true,
            maxTicksLimit: 2,
          },
        },
        y: {
          ticks: {
            callback: (value: number) => {
              return `${(value * 100).toFixed(0)}%`;
            },
            stepSize: 0.1,
            min: 0,
            max: 1,
          },
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 3,
        },
      },
    };
  }, []);

  const fieldData = useMemo(() => {
    let data;
    switch (selectedMetric) {
      case 'automation':
        data = fieldAutomationPerDay?.[selectedField ?? ''] ?? [];
        break;
      case 'flagged':
        data = fieldFlaggedPerDay?.[selectedField ?? ''] ?? [];
        break;
      case 'edits':
        data = fieldEditsPerDay?.[selectedField ?? ''] ?? [];
        break;
      default:
        data = fieldAutomationPerDay?.[selectedField ?? ''] ?? [];
    }

    return {
      labels: fieldLabels,
      datasets: [
        {
          data,
          borderColor: '#84CAFF',
          borderWidth: 1,
          backgroundColor: '#84CAFF',
          fill: true,
          tension: 0.2,
          pointRadius: 0,
          pointBackgroundColor: '#84CAFF',
        },
      ],
    };
  }, [fieldAutomationPerDay, fieldEditsPerDay, fieldFlaggedPerDay, fieldLabels, selectedField, selectedMetric]);

  const { valueText, changeText, change, datapoints } = useMemo(() => {
    const rowData = rows.find((row) => row.id === selectedField)?.[selectedMetric];
    const valueText = `${((rowData?.value ?? 0) * 100).toFixed(1)}%`;
    const changeText = `${((rowData?.change ?? 0) * 100).toFixed(1)}%`;
    const change = rowData?.change ?? 0;
    const datapoints = rowData?.datapoints ?? 0;
    return { valueText, changeText, change, datapoints };
  }, [rows, selectedField, selectedMetric]);

  const lowerIsBetter = selectedMetric === 'flagged' || selectedMetric === 'edits';
  const changeVariant = useChangeVariant(change, lowerIsBetter);

  return (
    <div className={merge('rounded-lg border border-gray-200 shadow-xs', className)} {...rest}>
      <div className="flex items-center rounded-t-lg border-b border-gray-200 bg-gray-50 p-1">
        <Select
          value={selectedField}
          onChange={(field) => {
            setSelectedField(field?.toString());
          }}
        >
          <div>
            <Button variant="secondary">
              <SelectValue />
              <ChevronIcon rotation="down" />
            </Button>
          </div>

          <Popover>
            <ListBox className="max-h-64 overflow-y-auto">
              {rows.map((row) => (
                <ListBoxItem key={row.id} id={row.id}>
                  <Text>{row.name}</Text>
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </Select>

        <div className="grow" />

        <Select
          value={selectedMetric}
          onChange={(metric) => {
            setSelectedMetric((metric?.toString() as MetricType | undefined) ?? 'automation');
          }}
        >
          <div>
            <Button variant="plain">
              <SelectValue />
              <ChevronIcon rotation="down" />
            </Button>
          </div>

          <Popover>
            <ListBox className="max-h-64 overflow-y-auto">
              {METRIC_TYPE_VALUES.map((metric) => (
                <ListBoxItem key={metric} id={metric}>
                  <Text>{statisticToName[metric]}</Text>
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </Select>
      </div>

      <div className="flex flex-col gap-2 bg-white p-2">
        <Flex className="items-center gap-2 p-2" variant="plain">
          <Text className="font-semibold text-gray-900" size="xl">
            {datapoints ? valueText : '--.-%'}
          </Text>
          {Math.abs(change) >= 0.01 ? (
            <Flex>
              <ArrowIcon variant={changeVariant} rotation={change > 0 ? 'up' : 'down'} />
              <Text variant={changeVariant} className="font-medium">
                {changeText}
              </Text>
            </Flex>
          ) : null}
        </Flex>

        <Line options={fieldOptions} data={fieldData} />
      </div>
    </div>
  );
};

export const FieldGraph = memo(IFieldGraph) as typeof IFieldGraph;
